export type AgriTourDraftPayload = {
  draftToken: string;
  resumeUrl: string;
  expiresAt: string;
  stepIndex: number;
  answers: Record<string, unknown>;
};

export const AGRITOUR_DRAFT_TOKEN_KEY = 'agritour_draft_token';

async function parseErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string | string[] };
    const message = body.message;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message[0]) return String(message[0]);
  } catch {
    // ignore
  }
  return fallback;
}

export async function createAgriTourDraft(body: {
  answers: Record<string, unknown>;
  stepIndex: number;
}): Promise<AgriTourDraftPayload> {
  const res = await fetch('/api/agritour/drafts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, 'שגיאה בשמירת הטיוטה'));
  }
  return res.json() as Promise<AgriTourDraftPayload>;
}

export async function updateAgriTourDraft(
  token: string,
  body: { answers: Record<string, unknown>; stepIndex: number },
): Promise<AgriTourDraftPayload> {
  const res = await fetch(`/api/agritour/drafts/${token}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, 'שגיאה בעדכון הטיוטה'));
  }
  return res.json() as Promise<AgriTourDraftPayload>;
}

export async function loadAgriTourDraft(token: string): Promise<AgriTourDraftPayload> {
  const res = await fetch(`/api/agritour/drafts/${token}`, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, 'שגיאה בטעינת הטיוטה'));
  }
  return res.json() as Promise<AgriTourDraftPayload>;
}

export function getStoredAgriTourDraftToken(): string | null {
  try {
    return sessionStorage.getItem(AGRITOUR_DRAFT_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredAgriTourDraftToken(token: string | null): void {
  try {
    if (token) {
      sessionStorage.setItem(AGRITOUR_DRAFT_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(AGRITOUR_DRAFT_TOKEN_KEY);
    }
  } catch {
    // ignore
  }
}

export async function saveAgriTourDraft(body: {
  answers: Record<string, unknown>;
  stepIndex: number;
  draftToken?: string | null;
}): Promise<AgriTourDraftPayload> {
  if (body.draftToken) {
    return updateAgriTourDraft(body.draftToken, {
      answers: body.answers,
      stepIndex: body.stepIndex,
    });
  }
  return createAgriTourDraft({
    answers: body.answers,
    stepIndex: body.stepIndex,
  });
}
